import { Button, Field, FieldLabel, Input, Spinner } from "@forge/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { Flame } from "lucide-react";
import { useState } from "react";
import { api, unwrap } from "../lib/api";
import { meQueryOptions } from "../lib/auth";

export const Route = createFileRoute("/accept-invite")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  component: AcceptInvitePage,
});

function apiErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === "object" && "value" in error) {
    const value = (error as { value: unknown }).value;
    if (
      value &&
      typeof value === "object" &&
      "error" in value &&
      typeof (value as { error: unknown }).error === "string"
    ) {
      return (value as { error: string }).error;
    }
  }
  return "Não foi possível aceitar o convite.";
}

function AcceptInvitePage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const me = useQuery({ ...meQueryOptions, retry: false });
  const preview = useQuery({
    queryKey: ["invite", token],
    enabled: token.length >= 16,
    queryFn: async () => unwrap(await api.auth.invite.get({ query: { token } })),
  });

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const existingUser = preview.data?.existingUser ?? false;
  const sameSession =
    existingUser &&
    me.data?.user.email != null &&
    me.data.user.email === preview.data?.email;

  const accept = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.auth["accept-invite"].post({
          token,
          name: existingUser ? undefined : name,
          password: sameSession ? undefined : password,
          acceptedTerms: existingUser ? undefined : true,
        }),
      ),
    onSuccess: async () => {
      queryClient.clear();
      await router.invalidate();
      await navigate({ to: "/home" });
    },
  });

  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface p-4">
      <div className="w-full max-w-xs">
        <div className="mb-8 flex flex-col items-center gap-3">
          <Link
            to="/"
            className="flex size-10 items-center justify-center rounded-surface bg-ember text-ink-inverse shadow-raised"
          >
            <Flame className="size-5" />
          </Link>
          <div className="text-center">
            <h1 className="text-lg font-semibold tracking-tight text-ink">
              Convite para a equipe
            </h1>
            <p className="text-2xs text-ink-muted">
              {preview.data
                ? `${preview.data.tenantName} · ${preview.data.email}`
                : "Entre com o link enviado por e-mail."}
            </p>
          </div>
        </div>

        {!token ? (
          <p className="rounded-control border border-danger/25 bg-danger-subtle px-3 py-2 text-2xs text-danger">
            Este link está incompleto. Peça um novo convite ao dono da loja.
          </p>
        ) : preview.isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner className="size-5 text-ink-muted" />
          </div>
        ) : preview.isError ? (
          <p className="rounded-control border border-danger/25 bg-danger-subtle px-3 py-2 text-2xs text-danger">
            {apiErrorMessage(preview.error)}
          </p>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              accept.mutate();
            }}
          >
            {!existingUser && (
              <Field>
                <FieldLabel>Seu nome</FieldLabel>
                <Input
                  required
                  minLength={2}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Maria Silva"
                />
              </Field>
            )}
            {!sameSession && (
              <Field>
                <FieldLabel>
                  {existingUser ? "Senha da conta" : "Senha"}
                </FieldLabel>
                <Input
                  type="password"
                  autoComplete={existingUser ? "current-password" : "new-password"}
                  required
                  minLength={existingUser ? 1 : 8}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={
                    existingUser ? "Senha atual" : "Mínimo 8 caracteres"
                  }
                />
              </Field>
            )}
            {!existingUser && (
              <label className="flex items-start gap-2 text-2xs leading-relaxed text-ink-secondary">
                <input
                  type="checkbox"
                  required
                  checked={acceptedTerms}
                  onChange={(event) => setAcceptedTerms(event.target.checked)}
                  className="mt-0.5 size-3.5 shrink-0 accent-ember"
                />
                <span>
                  Li e aceito os{" "}
                  <Link
                    to="/terms"
                    className="text-ink underline-offset-2 hover:underline"
                  >
                    termos de uso
                  </Link>{" "}
                  e a{" "}
                  <Link
                    to="/privacy"
                    className="text-ink underline-offset-2 hover:underline"
                  >
                    política de privacidade
                  </Link>
                  .
                </span>
              </label>
            )}
            {accept.isError && (
              <p className="rounded-control border border-danger/25 bg-danger-subtle px-3 py-2 text-2xs text-danger">
                {apiErrorMessage(accept.error)}
              </p>
            )}
            <Button
              type="submit"
              size="lg"
              disabled={!existingUser && !acceptedTerms}
              loading={accept.isPending}
            >
              Entrar na equipe
            </Button>
          </form>
        )}

        <p className="mt-6 text-center text-2xs text-ink-muted">
          <Link to="/login" className="text-ink underline-offset-2 hover:underline">
            Voltar ao login
          </Link>
        </p>
      </div>
    </div>
  );
}
